import {
  FloatingArrow,
  FloatingPortal,
  Placement,
  arrow as setArrow,
  autoUpdate,
  flip,
  offset,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  FloatingDelayGroup
} from '@floating-ui/react'
import React, { useRef } from 'react'
import styled from 'styled-components'
import { useFloatingPopoverController } from './FloatingPopover'
import { textSize } from './Text'

interface Props {
  className?: string
  content: React.ReactNode
  children?: React.ReactElement
  placement?: Placement
  arrow?: boolean
  visible?: boolean
  delay?: { open: number; close: number } | number
  withHover?: boolean
  onHidden?: () => void
}

const Content = styled.div`
  ${textSize('pxs')};
  width: max-content;
  border-radius: 4px;
`

const Wrapper = styled.div`
  width: max-content;
  background-color: #444;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
`

export const TooltipText = ({
  className,
  content,
  children,
  delay,
  placement = 'bottom',
  visible = true,
  withHover = true,
  onHidden,
  arrow
}: Props) => {
  const controls = useFloatingPopoverController()
  const arrowRef = useRef(null)

  const changeVisibility = (open: boolean) => {
    if (!open) {
      onHidden?.()
    }
    controls.setOpen(open)
  }

  const { refs, floatingStyles, context } = useFloating({
    open: controls.open,
    onOpenChange: changeVisibility,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), setArrow({ element: arrowRef }), flip({ fallbackAxisSideDirection: 'start' }), shift()]
  })

  const click = useClick(context)
  const hover = useHover(context, { move: false, delay, enabled: withHover, handleClose: safePolygon() })
  const focus = useFocus(context)
  const dismiss = useDismiss(context, {
    outsidePress: true,
    outsidePressEvent: 'click'
  })
  const role = useRole(context, { role: 'tooltip' })
  const interactions = [hover, click, focus, dismiss, role]

  const { getReferenceProps, getFloatingProps } = useInteractions(interactions)

  const childrenWithProps = children
    ? React.cloneElement(children, {
        ref: refs.setReference,
        ...getReferenceProps({
          onClick(e) {
            // Prevents onClick from being overwritten
            children.props.onClick?.(e)
          },
          onMouseDown(e) {
            children.props.onMouseDown?.(e)
          },
          onMouseUp(e) {
            children.props.onMouseUp?.(e)
          },
          onTouchStart(e) {
            children.props.onTouchStart?.(e)
          },
          onTouchEnd(e) {
            children.props.onTouchEnd?.(e)
          }
        })
      })
    : null

  return (
    <>
      {childrenWithProps}
      <FloatingDelayGroup delay={delay || 0}>
        <FloatingPortal>
          {controls.open && visible && (
            <Content className="Tooltip" ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
              <Wrapper className={className}>
                {arrow && <FloatingArrow fill="#444" strokeWidth={1} ref={arrowRef} context={context} />}
                {content}
              </Wrapper>
            </Content>
          )}
        </FloatingPortal>
      </FloatingDelayGroup>
    </>
  )
}
